import type { ViewStyle } from 'react-native';
import type { NativeStackNavigationOptions } from '@react-navigation/native-stack';
import { layout } from '../theme/tokens';

/**
 * BORNE DE LARGEUR DU CONTENU — le point d'enveloppe unique de l'application.
 *
 * Elle est posée ICI, et non dans les trente-quatre écrans, parce que c'est le seul objet que
 * TOUTES les piles partagent déjà : `RootNavigator`, `OnboardingNavigator`, `CvNavigator`,
 * `ProfileNavigator`, `CoachingNavigator` et `TermsGateNavigator` importent tous
 * `STACK_SCREEN_OPTIONS`. Un écran ajouté demain hérite de la borne sans que personne y pense —
 * c'est la seule forme d'enveloppe qui ne se contourne pas par oubli.
 *
 * `width: '100%'` EST INDISPENSABLE À CÔTÉ DE `alignSelf`. Seul, `alignSelf: 'center'` ferait
 * rétrécir le conteneur au contenu ; avec lui, l'écran occupe toute la largeur disponible jusqu'à
 * la borne, puis se centre.
 *
 * Sur mobile la borne est inerte : 430 pt au maximum contre 1200 (voir `layout` dans les tokens).
 */
export const CONTENU_BORNE: ViewStyle = {
  width: '100%',
  maxWidth: layout.contentMaxWidth,
  alignSelf: 'center',
};

/**
 * Exception à la borne, pour un seul écran : celui qui porte le NAVIGATEUR D'ONGLETS.
 *
 * La barre du bas est rendue par react-navigation en FRÈRE du conteneur d'écrans, donc à
 * l'intérieur de cet écran-ci. Bornée avec lui, sa surface s'arrêterait à 1200 px et flotterait
 * au milieu de la fenêtre, coupée du bord bas. On laisse donc `Main` occuper toute la largeur, et
 * la barre gère sa propre largeur intérieure (`BottomNav`).
 *
 * LA BORNE N'EST PAS REPRISE SUR LES SCÈNES D'ONGLETS, et ce point a coûté un défaut visuel :
 * une scène bornée est transparente sur ses marges, et les onglets inactifs continuent de peindre
 * derrière elle sur le web (voir `TabNavigator`). Les scènes traversent donc, et la borne vit sur
 * leur CONTENU.
 */
export const PLEINE_LARGEUR: NativeStackNavigationOptions = {
  contentStyle: { width: '100%' },
};

/**
 * Options communes à toutes les piles.
 *
 * `fullScreenGestureEnabled` est le seul réglage qui délivre réellement le retour par balayage
 * « sans viser » : `gestureEnabled` vaut déjà `true` par défaut, mais le balayage n'est reconnu que
 * depuis le BORD GAUCHE sur iOS 18 et antérieurs (il n'est plein écran par défaut qu'à partir
 * d'iOS 26). Sans cette option, on remplace une cible de 44 points par une autre.
 *
 * ANDROID : sans effet. `gestureEnabled` et `fullScreenGestureEnabled` sont marqués `@platform ios`
 * dans native-stack — un native-stack n'expose aucun geste de retour sur Android, où le retour passe
 * par le bouton système. Rien ici ne le couvre, et rien ne peut le couvrir sans changer de pile.
 */
export const STACK_SCREEN_OPTIONS: NativeStackNavigationOptions = {
  headerShown: false,
  fullScreenGestureEnabled: true,
  contentStyle: CONTENU_BORNE,
};

/**
 * OPTIONS DES SCÈNES D'ONGLETS — et l'invariant dont dépend tout le rendu web.
 *
 * L'INVARIANT : une scène d'onglet doit rester PLEINE LARGEUR ET OPAQUE, d'un bord à l'autre.
 *
 * POURQUOI. Sur le web, `react-native-screens` n'a pas d'implémentation : `MaybeScreen` retombe sur
 * une `View` ordinaire et ignore `activityState`. Les quatre onglets sont empilés en
 * `StyleSheet.absoluteFill`, les inactifs en `zIndex: -1`, et **ils continuent de peindre** —
 * inertes au clic (`aria-hidden`, `pointer-events: none`), mais bien visibles. Seule l'opacité de
 * la scène active les masque.
 *
 * CE QUI ARRIVE SI ON LA ROMPT : rien, sur l'écran qu'on est en train de regarder. Le défaut
 * apparaît AILLEURS — en deux rectangles blancs dans les coins des trois autres onglets, ce qui
 * s'est produit quand la bande d'en-tête de News est passée pleine largeur au-dessus d'une scène
 * bornée. C'est pourquoi cet objet est nommé, exporté, et gardé par un test : un réglage dont la
 * violation est invisible là où on la commet ne peut pas être laissé à la vigilance.
 *
 * NE JAMAIS Y METTRE de `sceneStyle` qui borne (`maxWidth`, `width`, `alignSelf`) ni de fond
 * transparent. La borne de largeur vit sur le CONTENU : `STACK_SCREEN_OPTIONS.contentStyle` pour
 * les onglets adossés à une pile, l'écran lui-même pour News et Offres.
 */
export const SCENES_ONGLETS = { headerShown: false } as const;

/**
 * Écrans où le balayage doit rester au bord. `TemplatePicker` y expose deux ScrollView horizontales
 * qui occupent toute la largeur : en plein écran, chaque glissement sur un carrousel entrerait en
 * concurrence avec le retour.
 */
export const EDGE_ONLY_GESTURE: NativeStackNavigationOptions = {
  fullScreenGestureEnabled: false,
};

/**
 * Étapes bloquantes (acceptation des CGU, complétion du profil). Aujourd'hui `RootNavigator` ne
 * monte qu'un écran à la fois : il n'y a rien à dépiler, donc rien à contourner. On l'écrit quand
 * même — la garantie ne doit pas dépendre d'une structure de navigateur qui peut changer sans que
 * personne ne refasse le raisonnement.
 */
export const NO_GESTURE: NativeStackNavigationOptions = {
  gestureEnabled: false,
};
